"use strict";

var inherits = require('inherits');
var Emitter = require('events').EventEmitter;
var bind = require('lodash.bind');
var isEmpty = require('lodash.isempty');
var FormScraper = require('ui-formscraper');
var validator = require('ui-validator');
var formatter = require('ui-formatter');
var extend = require('extend');
var defaultOpts = {
  validatorSchema: {},
  formatterSchema: {},
  formatEventTrigger: 'input'
};

/**
 * Form validator
 * Validates a form based on a schema using validate.js
 * @param {jQuery.Form} form
 */
var FormValidator = function FormValidator(form, options) {
  var tempOptions = options;
  if (!(this instanceof FormValidator)) {
    return new FormValidator(form, options);
  }

  // backward compatibility
  if (options && options.schema && !options.validatorSchema) {
    tempOptions.validatorSchema = options.schema;
  }
  this.options = extend({}, defaultOpts, tempOptions);
  this.form = form;
  this.scraper = new FormScraper(form);
  this.transformFunction = this.options.transformFunction;

  // On submit handler
  form.on('submit', bind(this.submitHandler, this));
  this.registerFormatterListeners();
};
inherits(FormValidator, Emitter);

/**
 * setModel populates the form using form-scraper
 * @param {Object} values  following form-scraper model format
 */
FormValidator.prototype.setModel = function setModel(values) {
  this.scraper.setModel(values);
};

/**
 * getModel populates the form using form-scraper
 * return {Object} values following form-scraper model format
 */
FormValidator.prototype.getModel = function getModel() {
  return this.scraper.getModel();
};

/**
 * submitHandler for submiting the form element to validate
 * Prevent defaults and emit error or success
 * @param  {jQuery.Event} e
 */
FormValidator.prototype.submitHandler = function submitHandler(e) {
  e.preventDefault();
  this.validate();
};

/**
 * validates the form element
 * Prevent defaults and emit error or success
 * @property fields {Array} optional fields to validate, if not provided, whole form is validated
 * @property {Object} [model] form data. If undefined, getModel() is called to scrape the form data
 * @emits FormValidator#error
 * @emits FormValidator#success
 * @emits FormValidator#successFields
 */
FormValidator.prototype.validate = function validate(fields, model) {
  var formData;
  var schema;
  formData = model || this.getModel();
  if (this.transformFunction && typeof this.transformFunction === 'function') {
    formData = this.transformFunction(formData);
  }
  schema = this.options.validatorSchema;
  if (!isEmpty(fields)) {
    schema = {};
    for (var i = 0; i < fields.length; i += 1) {
      schema[fields[i]] = this.options.validatorSchema[fields[i]];
    }
  }
  var unformatted = this.unformat(fields, formData);
  var errors = validator.validate(unformatted, schema);
  if (!isEmpty(errors)) {
    if (isEmpty(fields)) {
      /**
       * error event for entire form validation
       * @event FormValidator#error
       * @type {Object}
       * @property {Object} errors generated by validate.js
       */
      this.emit('error', errors);
    } else {
      /**
       * error event for individual field validation
       * @event FormValidator#errorFields
       * @type {Object}
       * @property {Array} fields array passed in by consumer
       * @property {Object} errors generated by validate.js
       */
      this.emit('errorFields', fields, errors);
    }
  } else if (isEmpty(fields)) {
    /**
     * success event
     * @event FormValidator#success
     * @type {Object}
     * @property {Object} formData generated by form-scraper
     */
    this.emit('success', formData);
  } else {
    /**
     * successFields event
     * @event FormValidator#successFields
     * @type {Object}
     * @property {Array} fields array passed in by consumer
     */
    this.emit('successFields', fields);
  }
};

/**
 * Registers event handlers for options.formatEventTrigger to format
 * fields listed in the formatterSchema
 */
FormValidator.prototype.registerFormatterListeners = function registerFormatterListeners() {
  var _this = this;
  var fSchema = this.options.formatterSchema;
  Object.keys(fSchema).forEach(function (key) {
    if (fSchema[key].formatter) {
      _this.form.find("[name=".concat(key, "]")).on(_this.options.formatEventTrigger, bind(_this.formatField, _this));
    }
  });
};

/**
 * Formats (customer formatting) a specific form field, placing the insertion
 * caret in the right position after formatting as needed.
 *
 * @property {Event} DOM event on the field being formatted
 */
FormValidator.prototype.formatField = function formatField(evt) {
  var element = evt.target;
  var caretPosition = element.selectionStart;
  var preformatted = element.value;
  var formData = {};
  formData[element.name] = preformatted;
  var formatted = this.format(element.name, formData);
  if (preformatted === element.value) {
    // make sure element value has not changed while running the formatter
    element.value = formatted[element.name];
    if (caretPosition > -1 && caretPosition < preformatted.length) {
      // don't bother if we're adding to the end
      element.setSelectionRange(caretPosition, caretPosition);
    }
  }
};

/**
 * Formats (customer formatting) form fields
 *
 * @property {Array or String} [fields] to format. If undefined, whole form is formatted per the formatSchema
 * @property {Object} [model] form data. If undefined, getModel() is called to scrape the form data
 * @emits FormValidator#formatted
 */
FormValidator.prototype.format = function format(fields, model) {
  var formData = model || this.getModel();
  var schema = this.options.formatterSchema;
  if (fields && !isEmpty(fields)) {
    schema = {};
    if (typeof fields === 'string') {
      schema[fields] = this.options.formatterSchema[fields];
    } else {
      for (var i = 0; i < fields.length; i += 1) {
        schema[fields[i]] = this.options.formatterSchema[fields[i]];
      }
    }
  }
  var formatted = formatter.format(formData, schema);
  this.emit('formatted', formatted, fields);
  return formatted;
};

/**
 * Unformats (submit formatting) form fields
 *
 * @property {Array or String} [fields] to unformat. If undefined, whole form is unformatted per the formatSchema
 * @property {Object} [model] form data. If undefined, getModel() is called to scrape the form data
 * @emits FormValidator#unformatted
 * @return {Object} Unformatted form data
 */
FormValidator.prototype.unformat = function unformat(fields, model) {
  var formData = model || this.getModel();
  var schema = this.options.formatterSchema;
  if (fields && !isEmpty(fields)) {
    schema = {};
    if (typeof fields === 'string') {
      schema[fields] = this.options.formatterSchema[fields];
    } else {
      for (var i = 0; i < fields.length; i += 1) {
        schema[fields[i]] = this.options.formatterSchema[fields[i]];
      }
    }
  }
  var unformatted = formatter.unformat(formData, schema);
  return unformatted;
};
module.exports = FormValidator;
